import type { EventMsg } from '~shared/services/eventsInbox/types';
import {
	DatadogLogger,
	LoggerMessages,
	LoggerServices,
} from '~shared/services/logger';
import { Client, RECONNECTION_FAILED_EVENT_TYPE } from '~utils/wscl';
import * as events from '~utils/wscl/events';

import { parseEvent } from './parse';

const MAX_RETRY = 5;

class WS {
	onConnectionFailed?: () => void;
	private getUrl?: () => Promise<string>;
	private wsc = new Client<string>({
		retryMax: MAX_RETRY,
		url: async () => {
			if (!this.getUrl) {
				throw new Error('you must pass url getter to connect!');
			}

			const url = await this.getUrl();
			if (!url) {
				DatadogLogger.error(
					LoggerServices.NetworkService,
					LoggerMessages.WebsocketConnectionFailed,
					{ error: new Error('empty url') }
				);
			}

			return url;
		},
	});

	constructor() {
		this.wsc.on(events.Error, (e) => {
			if (e.type === RECONNECTION_FAILED_EVENT_TYPE) {
				this.onConnectionFailed?.();
			}
		});
		this.wsc.on(events.Close, (e) => {
			// with any other code, reconnection will occur automatically
			// this check is left in case of an erroneous closing of the websocket
			// from the backend with code 1000 (websocket is no longer needed)
			if (e.code === 1000) {
				void this.connect();
			}
		});
	}

	async connect(
		getUrl?: () => Promise<string>,
		onConnectionFailed?: () => void
	) {
		if (getUrl) this.getUrl = getUrl;
		if (onConnectionFailed) this.onConnectionFailed = onConnectionFailed;

		try {
			await this.wsc.connect();
		} catch (error) {
			DatadogLogger.error(
				LoggerServices.NetworkService,
				LoggerMessages.WebsocketConnectionFailed,
				{ error }
			);
		}
	}

	onMsg(cb: (msg: EventMsg) => void) {
		return this.wsc.on(events.Message, (data) => {
			const msg = parseEvent(data);
			msg && cb(msg);
		});
	}

	close() {
		this.wsc.close();
	}
}

export * from '../eventsInbox/types';

export const ws = new WS();
